import React, { useState } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Button, HStack, useToast,
} from '@chakra-ui/react';
import {
  ImmOrden, crearOrden, actualizarOrden,
} from '../../../api/immApi';
import {
  SectionTitle, Field, EstatusBadge, fmt, ExpedienteSelect,
  IMM_COLOR, IMM_COLOR_HOVER,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: ImmOrden;
  onGuardado?: (o: ImmOrden) => void;
  onCancelar?: () => void;
}

const ESTATUS_OPCIONES = ['Vigente', 'Vencida', 'Cancelada'];
const hoy = () => new Date().toISOString().split('T')[0];

const FormularioOrden: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);
  const [expedienteId, setExpedienteId] = useState<number | undefined>(data?.expedienteId);

  const [f, setF] = useState<Partial<ImmOrden>>({
    numeroExpediente: data?.numeroExpediente || '',
    autoridadEmisora: data?.autoridadEmisora || '',
    fechaEmision: data?.fechaEmision ? data.fechaEmision.split('T')[0] : hoy(),
    fechaVencimiento: data?.fechaVencimiento ? data.fechaVencimiento.split('T')[0] : '',
    motivo: data?.motivo || '',
    tipoMedida: data?.tipoMedida || '',
    observaciones: data?.observaciones || '',
    estatus: data?.estatus || 'Vigente',
  });

  const set = (campo: keyof ImmOrden, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  if (modo === 'view') {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos de la orden</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Field label="Expediente" value={data?.expediente?.folio ?? (data ? `#${data.expedienteId}` : '—')} />
          <Field label="No. expediente" value={data?.numeroExpediente} />
          <Field label="Autoridad emisora" value={data?.autoridadEmisora} />
          <Field label="Tipo de medida" value={data?.tipoMedida} />
          <Field label="Fecha de emisión" value={fmt(data?.fechaEmision)} />
          <Field label="Fecha de vencimiento" value={fmt(data?.fechaVencimiento)} />
          <Field label="Estatus" value={<EstatusBadge value={data?.estatus} />} />
        </SimpleGrid>
        <SectionTitle>Detalle</SectionTitle>
        <Field label="Motivo" value={data?.motivo} />
        <Field label="Observaciones" value={data?.observaciones} />
      </VStack>
    );
  }

  const handleGuardar = async () => {
    if (modo === 'new' && !expedienteId) {
      toast({ title: 'El expediente es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmOrden> = {
        ...f,
        ...(modo === 'new' ? { expedienteId } : {}),
      };
      const res = modo === 'edit' && data
        ? await actualizarOrden(data.id, payload)
        : await crearOrden(payload);
      toast({ title: modo === 'edit' ? 'Orden actualizada' : 'Orden creada', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar la orden.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de la orden</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Expediente</FormLabel>
          <ExpedienteSelect value={expedienteId} onChange={setExpedienteId} isDisabled={modo !== 'new'} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">No. expediente</FormLabel>
          <Input value={f.numeroExpediente || ''} onChange={(e) => set('numeroExpediente', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Autoridad emisora</FormLabel>
          <Input value={f.autoridadEmisora || ''} onChange={(e) => set('autoridadEmisora', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha de emisión</FormLabel>
          <Input type="date" value={(f.fechaEmision as string) || ''} onChange={(e) => set('fechaEmision', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha de vencimiento</FormLabel>
          <Input type="date" value={(f.fechaVencimiento as string) || ''} onChange={(e) => set('fechaVencimiento', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de medida</FormLabel>
          <Input value={f.tipoMedida || ''} onChange={(e) => set('tipoMedida', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus || 'Vigente'} onChange={(e) => set('estatus', e.target.value)}>
            {ESTATUS_OPCIONES.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Motivo</FormLabel>
          <Textarea rows={3} value={f.motivo || ''} onChange={(e) => set('motivo', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={3} value={f.observaciones || ''} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar orden
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioOrden;
